import { apiGet, apiPost, apiPatch, apiDelete } from '@/lib/api/axios';
import {
  MeetingSeries,
  MeetingSeriesListResponse,
  Meeting,
  MeetingListResponse,
  MeetingDetail,
  MeetingResource,
  MeetingAttendee,
  MeetingApproval,
  CreateMeetingSeriesRequest,
  UpdateMeetingSeriesRequest,
  CreateMeetingRequest,
  UpdateMeetingRequest,
  AddMeetingResourceRequest,
  AttendeeStatusUpdate,
  SuggestedNextDateResponse,
  SuggestedAttendanceResponse,
  ActionSummaryResponse
} from './types';

// ============================================================================
// Series (group-scoped)
// ============================================================================

export async function createMeetingSeries(groupId: string, data: CreateMeetingSeriesRequest): Promise<MeetingSeries> {
  const response = await apiPost<MeetingSeries>(`/team-groups/${groupId}/meeting-series`, data, {
    meta: { globalError: true, context: 'meetings-create-series' }
  });
  return response.data;
}

export async function listMeetingSeries(groupId: string): Promise<MeetingSeriesListResponse> {
  const response = await apiGet<MeetingSeriesListResponse>(`/team-groups/${groupId}/meeting-series`, {
    meta: { globalError: true, context: 'meetings-list-series' }
  });
  return response.data;
}

export async function updateMeetingSeries(
  groupId: string,
  seriesId: string,
  data: UpdateMeetingSeriesRequest
): Promise<MeetingSeries> {
  const response = await apiPatch<MeetingSeries>(`/team-groups/${groupId}/meeting-series/${seriesId}`, data, {
    meta: { globalError: true, context: 'meetings-update-series' }
  });
  return response.data;
}

export async function archiveMeetingSeries(groupId: string, seriesId: string): Promise<void> {
  await apiDelete(`/team-groups/${groupId}/meeting-series/${seriesId}`, {
    meta: { globalError: true, context: 'meetings-archive-series' }
  });
}

export async function getSuggestedNextDate(
  groupId: string,
  seriesId: string,
  options?: { fromDate?: string }
): Promise<SuggestedNextDateResponse> {
  const params = new URLSearchParams();
  if (options?.fromDate) {
    params.set('from_date', options.fromDate);
  }
  const query = params.toString();
  const response = await apiGet<SuggestedNextDateResponse>(
    `/team-groups/${groupId}/meeting-series/${seriesId}/next-date${query ? `?${query}` : ''}`,
    { meta: { globalError: true, context: 'meetings-suggested-date' } }
  );
  return response.data;
}

// ============================================================================
// Meetings (group-scoped creation, top-level detail)
// ============================================================================

export async function createMeeting(groupId: string, data: CreateMeetingRequest): Promise<Meeting> {
  const response = await apiPost<Meeting>(`/team-groups/${groupId}/meetings`, data, {
    meta: { globalError: true, context: 'meetings-create' }
  });
  return response.data;
}

export async function listMeetings(
  groupId: string,
  statusFilter?: string[],
  options?: {
    startDate?: string;
    endDate?: string;
    limit?: number;
    upcomingPerSeries?: number;
    completedPerSeries?: number;
    upcomingOffset?: number;
    completedOffset?: number;
  }
): Promise<MeetingListResponse> {
  const params = new URLSearchParams();
  statusFilter?.forEach((s) => params.append('status', s));
  if (options?.startDate) params.set('start_date', options.startDate);
  if (options?.endDate) params.set('end_date', options.endDate);
  if (options?.limit != null) params.set('limit', String(options.limit));
  if (options?.upcomingPerSeries != null) params.set('upcoming_per_series', String(options.upcomingPerSeries));
  if (options?.completedPerSeries != null) params.set('completed_per_series', String(options.completedPerSeries));
  if (options?.upcomingOffset) params.set('upcoming_offset', String(options.upcomingOffset));
  if (options?.completedOffset) params.set('completed_offset', String(options.completedOffset));
  const query = params.toString();
  const url = query ? `/team-groups/${groupId}/meetings?${query}` : `/team-groups/${groupId}/meetings`;
  const response = await apiGet<MeetingListResponse>(url, {
    meta: { globalError: true, context: 'meetings-list' }
  });
  return response.data;
}

export async function getMeetingDetail(meetingId: string): Promise<MeetingDetail> {
  const response = await apiGet<MeetingDetail>(`/meetings/${meetingId}`, {
    meta: { globalError: true, context: 'meetings-detail' }
  });
  return response.data;
}

export async function updateMeeting(meetingId: string, data: UpdateMeetingRequest): Promise<Meeting> {
  const response = await apiPatch<Meeting>(`/meetings/${meetingId}`, data, {
    meta: { globalError: true, context: 'meetings-update' }
  });
  return response.data;
}

export async function archiveMeeting(meetingId: string): Promise<void> {
  await apiPost(`/meetings/${meetingId}/archive`, {}, { meta: { globalError: true, context: 'meetings-archive' } });
}

// ============================================================================
// Meeting sub-resources (top-level)
// ============================================================================

export async function addMeetingResource(meetingId: string, data: AddMeetingResourceRequest): Promise<MeetingResource> {
  const response = await apiPost<MeetingResource>(`/meetings/${meetingId}/resources`, data, {
    meta: { globalError: true, context: 'meetings-add-resource' }
  });
  return response.data;
}

export async function removeMeetingResource(meetingId: string, resourceId: string): Promise<void> {
  await apiDelete(`/meetings/${meetingId}/resources/${resourceId}`, {
    meta: { globalError: true, context: 'meetings-remove-resource' }
  });
}

export async function updateAttendance(
  meetingId: string,
  attendees: AttendeeStatusUpdate[]
): Promise<MeetingAttendee[]> {
  const response = await apiPatch<MeetingAttendee[]>(
    `/meetings/${meetingId}/attendance`,
    { attendees },
    { meta: { globalError: true, context: 'meetings-update-attendance' } }
  );
  return response.data;
}

export async function getSuggestedAttendance(
  meetingId: string,
  source: { externalWritingId: string } | { externalDocumentId: string }
): Promise<SuggestedAttendanceResponse> {
  const body =
    'externalWritingId' in source
      ? { external_writing_id: source.externalWritingId }
      : { external_document_id: source.externalDocumentId };
  const response = await apiPost<SuggestedAttendanceResponse>(`/meetings/${meetingId}/suggested-attendance`, body, {
    meta: { globalError: false, context: 'meetings-suggested-attendance' }
  });
  return response.data;
}

export async function sendMeetingInvites(meetingId: string): Promise<void> {
  await apiPost(
    `/meetings/${meetingId}/send-invites`,
    {},
    {
      meta: { globalError: true, context: 'meetings-send-invites' }
    }
  );
}

export async function sendMeetingReminder(meetingId: string): Promise<void> {
  await apiPost(
    `/meetings/${meetingId}/send-reminder`,
    {},
    {
      meta: { globalError: true, context: 'meetings-send-reminder' }
    }
  );
}

export interface SendSummaryRequest {
  notes?: string | null;
  next_meeting_date?: string | null;
}

export async function sendMeetingSummary(meetingId: string, data: SendSummaryRequest): Promise<void> {
  await apiPost(`/meetings/${meetingId}/send-summary`, data, {
    meta: { globalError: false, context: 'meetings-send-summary' }
  });
}

// ============================================================================
// Approvals
// ============================================================================

export async function cancelMeetingApproval(meetingId: string): Promise<void> {
  await apiDelete(`/meetings/${meetingId}/approvals`, {
    meta: { globalError: false, context: 'meetings-cancel-approval' }
  });
}

export async function requestMeetingApproval(
  meetingId: string,
  data: { approver_user_ids: number[] }
): Promise<MeetingApproval[]> {
  const response = await apiPost<MeetingApproval[]>(`/meetings/${meetingId}/approvals`, data, {
    meta: { globalError: false, context: 'meetings-request-approval' }
  });
  return response.data;
}

export async function updateMeetingApproval(
  meetingId: string,
  approvalId: string,
  data: { status: 'approved' | 'rejected'; rejection_reason?: string | null }
): Promise<MeetingApproval> {
  const response = await apiPatch<MeetingApproval>(`/meetings/${meetingId}/approvals/${approvalId}`, data, {
    meta: { globalError: false, context: 'meetings-update-approval' }
  });
  return response.data;
}

export async function getActionSummary(meetingId: string): Promise<ActionSummaryResponse> {
  const response = await apiGet<ActionSummaryResponse>(`/meetings/${meetingId}/action-summary`, {
    meta: { globalError: true, context: 'meetings-action-summary' }
  });
  return response.data;
}

export type MeetingMetaCounts = {
  present_attendees: number;
  expected_attendees: number;
  new_actions: number;
  updated_at_meeting: number;
  resolved_at_meeting: number;
  resolved_between: number;
  still_open: number;
};

type MeetingMetaResponse = {
  meta: Record<string, MeetingMetaCounts>;
};

const MEETING_META_BATCH_SIZE = 50;

/** Rows the group hub meetings list loads before "Show more". */
export const MEETING_LIST_INITIAL_UPCOMING = 3;
export const MEETING_LIST_INITIAL_PAST = 1;

export async function getBatchedMeetingMeta(groupId: string, meetingIds: string[]): Promise<MeetingMetaResponse> {
  const chunks: string[][] = [];
  for (let i = 0; i < meetingIds.length; i += MEETING_META_BATCH_SIZE) {
    chunks.push(meetingIds.slice(i, i + MEETING_META_BATCH_SIZE));
  }

  const responses = await Promise.all(
    chunks.map((chunk) =>
      apiPost<MeetingMetaResponse>(
        `/team-groups/${groupId}/meetings/meta`,
        { meeting_ids: chunk },
        {
          meta: { globalError: true, context: 'meeting-meta' }
        }
      )
    )
  );

  return { meta: Object.assign({}, ...responses.map((r) => r.data.meta)) };
}

export async function saveTranscriptAndGenerateMinutes(
  meetingId: string,
  transcript?: string
): Promise<{ external_writing_id: string; status: string }> {
  const body: Record<string, string> = {};
  if (transcript) {
    body.transcript = transcript;
  }
  const response = await apiPost<{ external_writing_id: string; status: string }>(
    `/meetings/${meetingId}/save-transcript`,
    body,
    { meta: { globalError: false, context: 'meetings-save-transcript' } }
  );
  return response.data;
}

export async function getDocumentOriginalLink(documentId: string): Promise<{ url: string; expiry_time: string }> {
  const response = await apiGet<{ url: string; expiry_time: string }>(`/files/${documentId}/link?original=true`, {
    meta: { globalError: false, context: 'meetings-document-link' }
  });
  return response.data;
}

export async function generateMeetingSummary(
  meetingId: string,
  source: { externalWritingId: string } | { externalDocumentId: string }
): Promise<{ summary: string | null }> {
  const body =
    'externalWritingId' in source
      ? { external_writing_id: source.externalWritingId }
      : { external_document_id: source.externalDocumentId };
  const response = await apiPost<{ summary: string | null }>(`/meetings/${meetingId}/summary`, body, {
    meta: { globalError: false, context: 'meetings-generate-summary' }
  });
  return response.data;
}

export interface DiarizationSegment {
  speaker: string | null;
  text: string;
}

export interface DiarizationResponse {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  segments: DiarizationSegment[] | null;
  error_message: string | null;
}

export async function startDiarization(meetingId: string): Promise<{ diarization_id: string; status: string }> {
  const response = await apiPost<{ diarization_id: string; status: string }>(
    `/meetings/${meetingId}/diarize`,
    {},
    { meta: { globalError: false, context: 'meetings-diarization' } }
  );
  return response.data;
}

export async function getDiarization(meetingId: string): Promise<DiarizationResponse> {
  const response = await apiGet<DiarizationResponse>(`/meetings/${meetingId}/diarization`, {
    meta: { globalError: false, context: 'meetings-diarization' }
  });
  return response.data;
}
